import React, { SyntheticEvent, useState } from 'react'
import { Box, Grid, Flex } from '@chatterbug/aaron'
import { Button } from '@chatterbug/theme'
import LanguageCardWithAvatarSkeleton from 'src/ui/LanguageCardWithAvatar/LanguageCardWithAvatarSkeleton'
import LanguageAvatar from 'src/ui/LanguageAvatar/LanguageAvatar'
import surveyData, {
  OnboardingQuestion,
  OnboardingReason,
  LearningReason,
} from './surveyData'
import { t } from 'src/lib/i18n-react'
import { LanguageCode } from 'src/types'
import { getLanguageName } from 'lib/util'
import { shuffle } from 'lodash'

export type Props = {
  targetLanguageCode: LanguageCode
  onSubmit: (
    question: OnboardingQuestion,
    answer: OnboardingReason,
    callback: (arg?: string) => void
  ) => void
  ctaText: string
}

const shuffledSurveyData = shuffle(surveyData)

export const findAnswer = (answer: string): LearningReason => {
  const a = surveyData.find((item) => item.answerName === answer)
  if (a) {
    return a
  } else {
    throw new Error('could not find LearningReason')
  }
}

const StudentOnboardingSurvey: React.FC<Props> = ({
  targetLanguageCode,
  onSubmit,
  ctaText,
}) => {
  const [hasDisabledButton, setHasDisabledButton] = useState(true)
  const [selectedAnswer, setAnswer] = useState(null)
  const [buttonText, setButtonText] = useState(ctaText)
  const [hasDisabledForm, setHasDisabledForm] = useState(false)

  const enableForm = () => {
    setHasDisabledForm(false)
    setHasDisabledButton(false)
    setButtonText(ctaText)
  }

  const disableForm = () => {
    setHasDisabledForm(true)
    setHasDisabledButton(true)
    setButtonText(t('saving...'))
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    disableForm()
    const a = findAnswer(selectedAnswer)
    onSubmit(a.question, a.answerName, enableForm)
  }

  return (
    <Flex flexDirection="column" alignItems="center">
      <h2 className="l-signup-page__title">
        {t('Why do you want to learn %{languageName}?', {
          languageName: getLanguageName(targetLanguageCode),
        })}
      </h2>
      <LanguageCardWithAvatarSkeleton
        open
        avatarSection={
          <LanguageAvatar languageCode={targetLanguageCode} variant="regular" />
        }
      >
        <Grid
          data-testid="form"
          as="form"
          gridGap="3x"
          onSubmit={handleSubmit}
          onChange={(e: SyntheticEvent) => {
            if (hasDisabledButton) {
              setHasDisabledButton(false)
            }
            const target = e.target as HTMLInputElement
            setAnswer(target.value)
          }}
        >
          <Flex
            as="fieldset"
            flexWrap="wrap"
            justifyContent="center"
            disabled={hasDisabledForm}
          >
            {shuffledSurveyData.map((answer) => {
              return (
                <React.Fragment key={`survey-answer-item-${answer.id}`}>
                  <Box
                    data-testid="survey-input"
                    variant="a11y.hidden"
                    as="input"
                    type="radio"
                    id={`survey-answer-item-${answer.id}`}
                    name="survey-answer"
                    value={answer.answerName}
                    defaultChecked={selectedAnswer === answer.answerName}
                    sx={{
                      '+ label': {
                        borderWidth: 'small',
                        borderStyle: 'solid',
                        borderColor: 'gray80',
                        borderRadius: '1,5x',
                        color: 'black19',
                      },
                      '&:hover + label, &:focus + label': {
                        borderColor: 'black0',
                        cursor: 'pointer',
                      },
                      '&:checked + label': {
                        color: 'pink51',
                        borderColor: 'transparent',
                        boxShadow: '0 0 0 2px currentcolor',
                        transition: 'all 0.25s',
                      },
                    }}
                  />
                  <Grid
                    data-testid="survey-input-label"
                    variant="text.paragraph"
                    gridTemplateColumns="auto 1fr"
                    gridGap="1x"
                    alignItems="center"
                    alignContent="center"
                    justifyItems="center"
                    mx="0,5x"
                    mt="2x"
                    as="label"
                    py="1x"
                    px="2x"
                    htmlFor={`survey-answer-item-${answer.id}`}
                    sx={{
                      '> svg.survey-icon': {
                        color: 'currentColor',
                      },
                    }}
                  >
                    {answer.icon}
                    {answer.answerText}
                  </Grid>
                </React.Fragment>
              )
            })}
          </Flex>
          <Flex justifyContent="center">
            <Button
              // This id is used for system tests
              id="submit-survey"
              data-testid="survey-submit"
              as="submit"
              type="submit"
              disabled={hasDisabledButton}
              kind="primary"
              presetColor="pink"
            >
              {buttonText}
            </Button>
          </Flex>
          <Box as="p" variant="text.subtitle" color="gray46">
            {t(
              'Don’t worry, this information is used to help tutors teach you the relevant content when you book a live lesson'
            )}
          </Box>
        </Grid>
      </LanguageCardWithAvatarSkeleton>
    </Flex>
  )
}

export default StudentOnboardingSurvey
